import { EndSessionRequest, IPublicClientApplication } from "@azure/msal-browser";
import { useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";
import { userSlice } from "../../features/user/UserStore";
import { useSomaAuth } from "../../features/auth/SomaAuthContext";
import { persistor } from "../../features/app/Store";
import { protectedResources } from "../../features/auth/authConfig";
import { applicationSessionChanged } from "../../features/app/store/GlobalActions";

const handleLogout = async (instance: IPublicClientApplication | undefined) => {
  if (!instance) {
    return;
  }

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const authenticationResult = account
    ? await instance.acquireTokenSilent({
        account,
        scopes: protectedResources.webAPI.scopes,
      })
    : undefined;

  return instance.logoutRedirect({
    idTokenHint: authenticationResult?.idToken,
    postLogoutRedirectUri: "/signed-out",
    mainWindowRedirectUri: "/signed-out",
  } as EndSessionRequest);
};

const clearPersistedApplicationState = async () => {
  // Prevent the reset user state from being written back while storage is purged.
  persistor.pause();
  await persistor.purge();
};

export const useLogout = () => {
  const dispatch = useDispatch();
  const { isLocalAuthEnabled, msalInstance, signOutFromLocalAuth } = useSomaAuth();

  const Logout = async () => {
    dispatch(applicationSessionChanged({ identityKey: null }));
    dispatch(userSlice.actions.reset());
    try {
      mixpanel.reset();
    } catch (e) {
      console.warn("Mixpanel.reset() didn't work, probably since mixpanel was not initialized yet", e);
    }

    await clearPersistedApplicationState();

    if (isLocalAuthEnabled) {
      sessionStorage.clear();
      signOutFromLocalAuth();
      window.location.replace("/signed-out");
      return;
    }

    // logoutRedirect clears MSAL's cached tokens and accounts before redirecting.
    // Clearing sessionStorage here would race the asynchronous MSAL logout flow.
    await handleLogout(msalInstance);
  };

  return Logout;
};
